import React, { ComponentProps, memo, ReactNode } from 'react'

import styled from '@emotion/styled'

import { WrapInToolTip } from '@surglogs-app/ui.tooltip'

import { FlexBox } from './FlexBox'
import { TextButton } from './TextButton'

interface TabButtonProps {
  active?: boolean
  children: ReactNode
}

export type Tab = ComponentProps<typeof TextButton> &
  Omit<TabButtonProps, 'variant' | 'children'> & {
    addonAfter?: ReactNode
    tooltipText?: ComponentProps<typeof WrapInToolTip>['tooltipText']
    'data-intercom-target'?: string
  }

const StyledTabListHeader = styled(FlexBox)`
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.ui.colors['navy.25']};
  gap: ${({ theme }) => theme.ui.spacings.xl};
`

const StyledTab = styled(TextButton)<{
  active?: boolean
}>`
  display: flex;
  gap: ${({ theme }) => theme.ui.spacings.sm};
  align-items: center;
  justify-content: center;
  position: relative;

  &:after {
    content: ' ';
    height: 1px;
    width: 100%;
    // 22px line-height + 16px padding
    top: ${({ theme, size = 'sm' }) =>
      `calc(${theme.ui.typography.lineHeights[size]} + ${theme.ui.spacings.md})`};
    left: 0;
    position: absolute;
    background-color: ${({ theme, active, disabled }) =>
      active && !disabled
        ? theme.ui.colors['blue.100']
        : theme.ui.colors['navy.25']};
  }
`

export interface TabListHeaderProps
  extends Omit<ComponentProps<typeof StyledTabListHeader>, 'variation'> {
  tabs: Tab[]
}

export const TabListHeader = memo(
  ({ tabs, flex = 1, px = 'xl', pb = 'md', ...rest }: TabListHeaderProps) => (
    <StyledTabListHeader {...{ flex, px, pb, ...rest }}>
      {tabs.map(({ id, addonAfter, tooltipText, ...tab }) => (
        <WrapInToolTip key={id} {...{ tooltipText }}>
          <StyledTab
            key={id}
            {...{
              ...tab,
              variant: tab.active ? 'primary' : 'default',
            }}>
            {id}
            {addonAfter}
          </StyledTab>
        </WrapInToolTip>
      ))}
    </StyledTabListHeader>
  ),
)
